import { registerCodes } from '@norkron/web-errors';
import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { useRef, useEffect } from 'react';

// src/error-codes.ts
var WEB_DISMISS_LIB_ID = "@norkron/web-dismiss";
try {
  registerCodes(WEB_DISMISS_LIB_ID, {
    WEB_DISMISS_BUTTON_NO_ACCESSIBLE_LABEL: {
      description: "selfCheck: a dismiss button (close / \xD7 / x) renders with no accessible name \u2014 neither visible text, `aria-label`, nor `aria-labelledby`. Screen-reader users cannot identify the control. (Conformance severity: major.)",
      severity: "warning",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-dismiss/WEB_DISMISS_BUTTON_NO_ACCESSIBLE_LABEL",
      exampleFix: 'Add `aria-label="Close"` (or the localised equivalent) to the dismiss button, or include visible button text.'
    },
    WEB_DISMISS_BUTTON_BELOW_MIN_TARGET: {
      description: "selfCheck: a dismiss button renders smaller than the WCAG 2.5.5 minimum touch-target size (24\xD724 CSS px advisory, 44\xD744 ideal). The control is hard to hit on touch / for users with motor impairments. (Conformance severity: major.)",
      severity: "warning",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-dismiss/WEB_DISMISS_BUTTON_BELOW_MIN_TARGET",
      exampleFix: "Increase the button hit area to at least 24\xD724 px (use padding, min-width/min-height, or an invisible expanded ::before hit area)."
    },
    WEB_DISMISS_SELFCHECK_INTERNAL_ERROR: {
      description: "selfCheck: an internal error was caught and surfaced as a non-throwing finding (selfCheck never throws). Indicates a defect in the selfCheck itself, not in consumer code. (Conformance severity: info.)",
      severity: "info",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-dismiss/WEB_DISMISS_SELFCHECK_INTERNAL_ERROR",
      exampleFix: "File an issue against @norkron/web-dismiss with the finding.message stack context."
    }
  });
} catch {
}

// src/self-check.ts
var LIB_ID = "@norkron/web-dismiss";
var MIN_TARGET_PX = 24;
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function hasAccessibleName(el) {
  const aria = el.getAttribute("aria-label");
  if (aria && aria.trim().length > 0) return true;
  const labelledby = el.getAttribute("aria-labelledby");
  if (labelledby && labelledby.trim().length > 0) {
    const ownerDoc = el.ownerDocument;
    if (ownerDoc) {
      for (const id of labelledby.split(/\s+/)) {
        const ref = ownerDoc.getElementById(id);
        if (ref && (ref.textContent ?? "").trim().length > 0) return true;
      }
    }
  }
  const text = (el.textContent ?? "").trim();
  if (text.length >= 2) return true;
  return false;
}
function selfCheck(root, opts) {
  const safeOpts = {
    budgetMs: opts?.budgetMs ?? 50,
    minSeverity: opts?.minSeverity ?? "info",
    locale: opts?.locale ?? "en"
  };
  const findings = [];
  try {
    const explicit = Array.from(
      root.querySelectorAll("[data-dismiss-button]")
    );
    const dedupe = new Set(explicit);
    for (const btn of root.querySelectorAll('button, [role="button"]')) {
      const label = (btn.getAttribute("aria-label") ?? "").toLowerCase();
      if (label.includes("close") || label.includes("dismiss") || label.includes("cancel") || label === "\xD7" || label === "\u2715") {
        dedupe.add(btn);
      }
    }
    const canMeasure = typeof window !== "undefined" && typeof window.getComputedStyle === "function";
    for (const btn of dedupe) {
      if (btn.hasAttribute("data-dismiss-button") && !hasAccessibleName(btn)) {
        findings.push({
          libId: LIB_ID,
          code: "WEB_DISMISS_BUTTON_NO_ACCESSIBLE_LABEL",
          category: "a11y",
          severity: "major",
          message: 'Dismiss-stamped element has no accessible name. Screen-reader users hear "button" with no purpose.',
          selector: selectorFor(btn),
          suggestedFix: 'Add aria-label="Close" (or the localised equivalent) or render visible text inside the button.'
        });
      }
      if (canMeasure) {
        const rect = btn.getBoundingClientRect();
        const w = rect.width;
        const h = rect.height;
        if (w > 0 && h > 0 && (w < MIN_TARGET_PX || h < MIN_TARGET_PX)) {
          findings.push({
            libId: LIB_ID,
            code: "WEB_DISMISS_BUTTON_BELOW_MIN_TARGET",
            category: "a11y",
            severity: "major",
            message: `Dismiss button computed size is ${Math.round(w)}x${Math.round(h)}px \u2014 below WCAG 2.2 SC 2.5.8 minimum of ${MIN_TARGET_PX}x${MIN_TARGET_PX}px.`,
            selector: selectorFor(btn),
            suggestedFix: "Enlarge the dismiss button via padding or min-width/min-height tokens so the hit area is at least 24x24 CSS pixels.",
            extras: { width: w, height: h, minPx: MIN_TARGET_PX }
          });
        }
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "WEB_DISMISS_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}

// src/dismiss-layer-stack.ts
var stack = [];
var nextId = 1;
function pushLayer(onEscape) {
  const id = nextId++;
  stack.push({ id, onEscape });
  return id;
}
function popLayer(id) {
  const idx = stack.findIndex((l) => l.id === id);
  if (idx >= 0) stack.splice(idx, 1);
}
function topLayerId() {
  const top = stack[stack.length - 1];
  return top ? top.id : null;
}
function fireTopEscape() {
  const top = stack[stack.length - 1];
  if (!top) return false;
  top.onEscape();
  return true;
}

// src/use-dismiss.ts
function useDismiss(opts) {
  const { enabled = true, onDismiss, refs, preventOutsideClickWhen } = opts;
  const latestRef = useRef({ onDismiss, refs, preventOutsideClickWhen });
  latestRef.current = { onDismiss, refs, preventOutsideClickWhen };
  const layerIdRef = useRef(null);
  useEffect(() => {
    if (!enabled) {
      layerIdRef.current = null;
      return;
    }
    const layerId = pushLayer(() => {
      latestRef.current.onDismiss("escape-key");
    });
    layerIdRef.current = layerId;
    const onPointerDown = (e) => {
      const { refs: r, preventOutsideClickWhen: gate, onDismiss: fire } = latestRef.current;
      if (gate && gate(e)) return;
      const target = e.target;
      if (!target) return;
      for (const ref of r) {
        const el = ref.current;
        if (el && (el === target || el.contains(target))) return;
      }
      fire("outside-pointer");
    };
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      if (topLayerId() !== layerId) return;
      e.stopPropagation();
      fireTopEscape();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
      popLayer(layerId);
      layerIdRef.current = null;
    };
  }, [enabled]);
  return layerIdRef.current;
}

// src/index.ts
registerLazy(
  "@norkron/web-dismiss",
  () => selfCheck
);

export { WEB_DISMISS_LIB_ID, selfCheck, topLayerId, useDismiss };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map